import { useCallback, useState } from 'react';
import {
  ALL_PATIENTS_CRITERIA,
  defaultCriteriaRow,
  defaultNormalRangeRow,
  parseAnalyteCodeString,
  joinAnalyteCodes,
  getConditionKey,
  parseCriteriaFromCutoff,
  serializeCriteriaToKeys,
  parseNormalRangeFromCutoff,
  serializeNormalRangeKey,
  isComparisonAllowedForCriteria,
  comparisonToConstraint,
  mergeConstraints,
  isConstraintValid,
} from './cutoffUtils';

const CUTOFF_DIALOG_MODE_ADD = 'add';
const CUTOFF_DIALOG_MODE_MODIFY = 'modify';

const cutoffTypeUiToApiValue = {
  single_analyte: 'value',
  ratio: 'ratio',
  sum: 'sum',
};

const cutoffTypeApiToUiValue = {
  value: 'single_analyte',
  SINGLE_ANALYTE: 'single_analyte',
  ratio: 'ratio',
  sum: 'sum',
};

const useCutoffEditor = ({
  cutoffValues,
  setCutoffValues,
  setBiochemicalError,
  tempConditionRows,
  setTempConditionRows,
}) => {
  const [cutoffDialogOpen, setCutoffDialogOpen] = useState(false);
  const [cutoffDialogMode, setCutoffDialogMode] = useState(CUTOFF_DIALOG_MODE_ADD);
  const [cutoffDialogCondition, setCutoffDialogCondition] = useState(null);
  const [cutoffDialogSelectedProfileIndex, setCutoffDialogSelectedProfileIndex] = useState(null);
  const [cutoffDialogValues, setCutoffDialogValues] = useState({
    cutoffType: '',
    analyteCode: '',
    analyteCodes: [],
    ratioNumeratorType: '',
    ratioNumeratorAnalyteCode: '',
    ratioNumeratorAnalyteCodes: [],
    ratioDenominatorType: '',
    ratioDenominatorAnalyteCode: '',
    ratioDenominatorAnalyteCodes: [],
    criteriaRows: [defaultCriteriaRow],
    normalRangeRows: [defaultNormalRangeRow],
    flagMessage: '',
  });
  const [cutoffDialogErrors, setCutoffDialogErrors] = useState({});
  const [cutoffDialogSaveAttempted, setCutoffDialogSaveAttempted] = useState(false);
  const [cutoffDialogSaving, setCutoffDialogSaving] = useState(false);
  const [ratioSelectionProgressUnlocked, setRatioSelectionProgressUnlocked] = useState(false);

  const getCutoffDialogTitle = useCallback(() => {
    const dialogAction = cutoffDialogMode === CUTOFF_DIALOG_MODE_MODIFY ? 'Modify' : 'Add';
    const conditionLabel = cutoffDialogCondition?.acronym || cutoffDialogCondition?.name || 'Condition';
    return `${dialogAction} Cutoff for ${conditionLabel}`;
  }, [cutoffDialogCondition, cutoffDialogMode]);

  const validateCutoffDialog = useCallback((values) => {
    const validationErrors = {};
    const criteriaRows = Array.isArray(values.criteriaRows) ? values.criteriaRows : [];

    if (!values.cutoffType) {
      validationErrors.cutoffType = 'Cutoff Type is required.';
    }

    if (values.cutoffType === 'single_analyte' && !values.analyteCode) {
      validationErrors.analyteCode = 'Analyte is required.';
    }

    if (values.cutoffType === 'sum' && (!Array.isArray(values.analyteCodes) || values.analyteCodes.length < 2)) {
      validationErrors.analyteCodes = 'Select at least 2 analytes.';
    }

    if (values.cutoffType === 'ratio') {
      if (!values.ratioNumeratorType) {
        validationErrors.ratioNumeratorType = 'Numerator is required.';
      }

      if (values.ratioNumeratorType === 'single_analyte' && !values.ratioNumeratorAnalyteCode) {
        validationErrors.ratioNumeratorAnalyteCode = 'Numerator Analyte is required.';
      }

      if (values.ratioNumeratorType === 'sum' && (!Array.isArray(values.ratioNumeratorAnalyteCodes) || values.ratioNumeratorAnalyteCodes.length < 2)) {
        validationErrors.ratioNumeratorAnalyteCodes = 'Select at least 2 numerator analytes.';
      }

      const numeratorReady = (
        (values.ratioNumeratorType === 'single_analyte' && !!values.ratioNumeratorAnalyteCode)
        || (values.ratioNumeratorType === 'sum' && Array.isArray(values.ratioNumeratorAnalyteCodes) && values.ratioNumeratorAnalyteCodes.length >= 2)
      );

      if (numeratorReady && !values.ratioDenominatorType) {
        validationErrors.ratioDenominatorType = 'Denominator is required.';
      }

      if (values.ratioDenominatorType === 'single_analyte' && !values.ratioDenominatorAnalyteCode) {
        validationErrors.ratioDenominatorAnalyteCode = 'Denominator Analyte is required.';
      }

      if (values.ratioDenominatorType === 'sum' && (!Array.isArray(values.ratioDenominatorAnalyteCodes) || values.ratioDenominatorAnalyteCodes.length < 2)) {
        validationErrors.ratioDenominatorAnalyteCodes = 'Select at least 2 denominator analytes.';
      }
    }

    const shouldValidateCriteriaSections = (
      (values.cutoffType === 'single_analyte' && !!values.analyteCode)
      || (values.cutoffType === 'sum' && Array.isArray(values.analyteCodes) && values.analyteCodes.length >= 2)
      || (
        values.cutoffType === 'ratio'
        && (
          (values.ratioDenominatorType === 'single_analyte' && !!values.ratioDenominatorAnalyteCode)
          || (values.ratioDenominatorType === 'sum' && Array.isArray(values.ratioDenominatorAnalyteCodes) && values.ratioDenominatorAnalyteCodes.length >= 2)
        )
      )
    );

    if (shouldValidateCriteriaSections) {
      const criteriaConstraintByType = {};

      if (criteriaRows.length === 0) {
        validationErrors['criteriaRows.0.criteria'] = 'Select Patient Criteria is required.';
      }

      criteriaRows.forEach((row, rowIndex) => {
        if (!row.criteria) {
          validationErrors[`criteriaRows.${rowIndex}.criteria`] = 'Select Patient Criteria is required.';
          return;
        }

        if (rowIndex > 0 && row.criteria === 'all_patients') {
          validationErrors[`criteriaRows.${rowIndex}.criteria`] = 'All Patients is only allowed on the first row.';
          return;
        }

        if (row.criteria === 'all_patients') {
          return;
        }

        const comparisonMissing = !row.comparison;
        const comparisonAllowed = comparisonMissing ? false : isComparisonAllowedForCriteria(row.criteria, row.comparison);

        if (comparisonMissing) {
          validationErrors[`criteriaRows.${rowIndex}.comparison`] = 'Select Comparison is required.';
        } else if (!comparisonAllowed) {
          validationErrors[`criteriaRows.${rowIndex}.comparison`] = 'This comparison is not allowed for the selected criteria.';
        }

        if (row.comparison === 'between') {
          const isBirthWeight = row.criteria === 'birth_weight';

          if (!row.betweenComparison1) {
            validationErrors[`criteriaRows.${rowIndex}.betweenComparison1`] = 'First comparison is required.';
          }

          if (row.betweenValue1 === '' || row.betweenValue1 === null || row.betweenValue1 === undefined) {
            validationErrors[`criteriaRows.${rowIndex}.betweenValue1`] = 'First amount is required.';
          } else if (Number.isNaN(Number(row.betweenValue1))) {
            validationErrors[`criteriaRows.${rowIndex}.betweenValue1`] = 'First amount must be numeric.';
          } else if (isBirthWeight && !Number.isInteger(Number(row.betweenValue1))) {
            validationErrors[`criteriaRows.${rowIndex}.betweenValue1`] = 'First amount must be a whole number of grams.';
          }

          if (!row.betweenComparison2) {
            validationErrors[`criteriaRows.${rowIndex}.betweenComparison2`] = 'Second comparison is required.';
          }

          if (row.betweenValue2 === '' || row.betweenValue2 === null || row.betweenValue2 === undefined) {
            validationErrors[`criteriaRows.${rowIndex}.betweenValue2`] = 'Second amount is required.';
          } else if (Number.isNaN(Number(row.betweenValue2))) {
            validationErrors[`criteriaRows.${rowIndex}.betweenValue2`] = 'Second amount must be numeric.';
          } else if (isBirthWeight && !Number.isInteger(Number(row.betweenValue2))) {
            validationErrors[`criteriaRows.${rowIndex}.betweenValue2`] = 'Second amount must be a whole number of grams.';
          }

          if (row.betweenComparison1 && !['greater_than', 'greater_than_or_equal_to', 'less_than', 'less_than_or_equal_to'].includes(row.betweenComparison1)) {
            validationErrors[`criteriaRows.${rowIndex}.betweenComparison1`] = 'Select a valid comparison for the between range.';
          }

          if (row.betweenComparison2 && !['greater_than', 'greater_than_or_equal_to', 'less_than', 'less_than_or_equal_to'].includes(row.betweenComparison2)) {
            validationErrors[`criteriaRows.${rowIndex}.betweenComparison2`] = 'Select a valid comparison for the between range.';
          }

          if (row.betweenComparison1 && row.betweenComparison2) {
            const isLowerBound = (c) => ['greater_than', 'greater_than_or_equal_to'].includes(c);
            const isUpperBound = (c) => ['less_than', 'less_than_or_equal_to'].includes(c);
            if ((isLowerBound(row.betweenComparison1) && isLowerBound(row.betweenComparison2))
                || (isUpperBound(row.betweenComparison1) && isUpperBound(row.betweenComparison2))) {
              validationErrors[`criteriaRows.${rowIndex}.betweenComparison2`] = 'Both comparisons cannot be in the same direction. One must be a lower bound and one an upper bound.';
            }
          }

          const firstConstraint = comparisonToConstraint(row.betweenComparison1, row.betweenValue1);
          const secondConstraint = comparisonToConstraint(row.betweenComparison2, row.betweenValue2);

          if (firstConstraint && secondConstraint) {
            const mergedBetween = mergeConstraints(firstConstraint, secondConstraint);
            if (!isConstraintValid(mergedBetween)) {
              validationErrors[`criteriaRows.${rowIndex}.betweenValue2`] = 'The between range is invalid. Ensure the lower bound is not greater than the upper bound.';
            } else {
              const existingConstraint = criteriaConstraintByType[row.criteria] || {
                lower: null,
                lowerInclusive: true,
                upper: null,
                upperInclusive: true,
              };
              const mergedCriteriaConstraint = mergeConstraints(existingConstraint, mergedBetween);

              if (!isConstraintValid(mergedCriteriaConstraint)) {
                validationErrors[`criteriaRows.${rowIndex}.betweenValue2`] = 'This criteria row conflicts with another row for the same patient criteria.';
              } else {
                criteriaConstraintByType[row.criteria] = mergedCriteriaConstraint;
              }
            }
          }
          return;
        }

        if (row.value === '' || row.value === null || row.value === undefined) {
          validationErrors[`criteriaRows.${rowIndex}.value`] = 'Amount is required.';
        } else if (Number.isNaN(Number(row.value))) {
          validationErrors[`criteriaRows.${rowIndex}.value`] = 'Amount must be numeric.';
        } else if (row.criteria === 'birth_weight' && !Number.isInteger(Number(row.value))) {
          validationErrors[`criteriaRows.${rowIndex}.value`] = 'Amount must be a whole number of grams.';
          return;
        }

        if (comparisonMissing || !comparisonAllowed) {
          return;
        }

        const rowConstraint = comparisonToConstraint(row.comparison, row.value);
        if (rowConstraint) {
          const existingConstraint = criteriaConstraintByType[row.criteria] || {
            lower: null,
            lowerInclusive: true,
            upper: null,
            upperInclusive: true,
          };
          const mergedCriteriaConstraint = mergeConstraints(existingConstraint, rowConstraint);

          if (!isConstraintValid(mergedCriteriaConstraint)) {
            validationErrors[`criteriaRows.${rowIndex}.value`] = 'This criteria row conflicts with another row for the same patient criteria.';
          } else {
            criteriaConstraintByType[row.criteria] = mergedCriteriaConstraint;
          }
        }
      });

      const normalRangeRows = Array.isArray(values.normalRangeRows) ? values.normalRangeRows : [];

      if (normalRangeRows.length === 0) {
        validationErrors['normalRangeRows.0.normalRangeOperator'] = 'Normal Range comparison is required.';
      }

      normalRangeRows.forEach((row, rowIndex) => {
        if (!row.normalRangeOperator) {
          validationErrors[`normalRangeRows.${rowIndex}.normalRangeOperator`] = 'Normal Range comparison is required.';
          return;
        }

        if (row.normalRangeOperator === 'between') {
          if (!row.normalRangeBetweenComparison1) {
            validationErrors[`normalRangeRows.${rowIndex}.normalRangeBetweenComparison1`] = 'First comparison is required.';
          }
          if (row.normalRangeBetweenValue1 === '' || row.normalRangeBetweenValue1 === null || row.normalRangeBetweenValue1 === undefined) {
            validationErrors[`normalRangeRows.${rowIndex}.normalRangeBetweenValue1`] = 'First amount is required.';
          } else if (Number.isNaN(Number(row.normalRangeBetweenValue1))) {
            validationErrors[`normalRangeRows.${rowIndex}.normalRangeBetweenValue1`] = 'First amount must be numeric.';
          }
          if (!row.normalRangeBetweenComparison2) {
            validationErrors[`normalRangeRows.${rowIndex}.normalRangeBetweenComparison2`] = 'Second comparison is required.';
          }
          if (row.normalRangeBetweenValue2 === '' || row.normalRangeBetweenValue2 === null || row.normalRangeBetweenValue2 === undefined) {
            validationErrors[`normalRangeRows.${rowIndex}.normalRangeBetweenValue2`] = 'Second amount is required.';
          } else if (Number.isNaN(Number(row.normalRangeBetweenValue2))) {
            validationErrors[`normalRangeRows.${rowIndex}.normalRangeBetweenValue2`] = 'Second amount must be numeric.';
          }
          if (row.normalRangeBetweenComparison1 && row.normalRangeBetweenComparison2) {
            const isLowerBound = (c) => ['greater_than', 'greater_than_or_equal_to'].includes(c);
            const isUpperBound = (c) => ['less_than', 'less_than_or_equal_to'].includes(c);
            if ((isLowerBound(row.normalRangeBetweenComparison1) && isLowerBound(row.normalRangeBetweenComparison2))
                || (isUpperBound(row.normalRangeBetweenComparison1) && isUpperBound(row.normalRangeBetweenComparison2))) {
              validationErrors[`normalRangeRows.${rowIndex}.normalRangeBetweenComparison2`] = 'Both comparisons cannot be in the same direction. One must be a lower bound and one an upper bound.';
            } else {
              const c1 = comparisonToConstraint(row.normalRangeBetweenComparison1, row.normalRangeBetweenValue1);
              const c2 = comparisonToConstraint(row.normalRangeBetweenComparison2, row.normalRangeBetweenValue2);
              if (c1 && c2 && !isConstraintValid(mergeConstraints(c1, c2))) {
                validationErrors[`normalRangeRows.${rowIndex}.normalRangeBetweenValue2`] = 'The between range is invalid. Ensure the lower bound is not greater than the upper bound.';
              }
            }
          }
          return;
        }

        if (row.normalRangeValue === '' || row.normalRangeValue === null || row.normalRangeValue === undefined) {
          validationErrors[`normalRangeRows.${rowIndex}.normalRangeValue`] = 'Normal Range value is required.';
        } else if (Number.isNaN(Number(row.normalRangeValue))) {
          validationErrors[`normalRangeRows.${rowIndex}.normalRangeValue`] = 'Normal Range value must be numeric.';
        }
      });

      if (!values.flagMessage || !values.flagMessage.trim()) {
        validationErrors.flagMessage = 'Flag Message is required.';
      }
    }

    return validationErrors;
  }, []);

  const openCutoffDialog = useCallback((mode, condition, profileIndex = null, cutoff = null) => {
    const parsedAnalyteCode1 = parseAnalyteCodeString(cutoff?.analyteCode1);
    const parsedAnalyteCode2 = parseAnalyteCodeString(cutoff?.analyteCode2);
    const parsedAnalyteCodes = Array.isArray(cutoff?.analyteCodes)
      ? cutoff.analyteCodes
      : (parsedAnalyteCode1.length > 0 ? parsedAnalyteCode1 : [cutoff?.analyteCode1].filter(Boolean));
    const cutoffType = cutoffTypeApiToUiValue[cutoff?.cutoffType] || '';

    const parsedRows = cutoff
      ? parseCriteriaFromCutoff(cutoff)
      : [{ ...defaultCriteriaRow }];

    const parsedNormalRangeRows = cutoff
      ? parseNormalRangeFromCutoff(cutoff)
      : [{ ...defaultNormalRangeRow }];

    setCutoffDialogMode(mode);
    setCutoffDialogCondition(condition);
    setCutoffDialogSelectedProfileIndex(profileIndex);
    setCutoffDialogValues({
      cutoffType,
      analyteCode: parsedAnalyteCode1[0] || cutoff?.analyteCode1 || '',
      analyteCodes: parsedAnalyteCodes,
      ratioNumeratorType: cutoff?.ratioNumeratorType || (cutoffType === 'ratio' ? (parsedAnalyteCode1.length > 1 ? 'sum' : 'single_analyte') : ''),
      ratioNumeratorAnalyteCode: cutoff?.ratioNumeratorAnalyteCode || (cutoffType === 'ratio' ? (parsedAnalyteCode1[0] || '') : ''),
      ratioNumeratorAnalyteCodes: Array.isArray(cutoff?.ratioNumeratorAnalyteCodes)
        ? cutoff.ratioNumeratorAnalyteCodes
        : (cutoffType === 'ratio' && parsedAnalyteCode1.length > 1 ? parsedAnalyteCode1 : []),
      ratioDenominatorType: cutoff?.ratioDenominatorType || (cutoffType === 'ratio' ? (parsedAnalyteCode2.length > 1 ? 'sum' : 'single_analyte') : ''),
      ratioDenominatorAnalyteCode: cutoff?.ratioDenominatorAnalyteCode || (cutoffType === 'ratio' ? (parsedAnalyteCode2[0] || '') : ''),
      ratioDenominatorAnalyteCodes: Array.isArray(cutoff?.ratioDenominatorAnalyteCodes)
        ? cutoff.ratioDenominatorAnalyteCodes
        : (cutoffType === 'ratio' && parsedAnalyteCode2.length > 1 ? parsedAnalyteCode2 : []),
      criteriaRows: parsedRows,
      normalRangeRows: parsedNormalRangeRows,
      flagMessage: cutoff?.flagMessage || '',
    });
    setCutoffDialogErrors({});
    setCutoffDialogSaveAttempted(false);
    // In modify mode the ratio is already fully configured, so unlock immediately
    // to prevent cascade-clearing denominator/criteria when numerator is edited.
    setRatioSelectionProgressUnlocked(mode === CUTOFF_DIALOG_MODE_MODIFY);
    setCutoffDialogOpen(true);
  }, []);

  const closeCutoffDialog = useCallback(() => {
    setCutoffDialogOpen(false);
    setCutoffDialogErrors({});
    setCutoffDialogSaving(false);
    setCutoffDialogCondition(null);
    setCutoffDialogSelectedProfileIndex(null);
    setCutoffDialogValues({
      cutoffType: '',
      analyteCode: '',
      analyteCodes: [],
      ratioNumeratorType: '',
      ratioNumeratorAnalyteCode: '',
      ratioNumeratorAnalyteCodes: [],
      ratioDenominatorType: '',
      ratioDenominatorAnalyteCode: '',
      ratioDenominatorAnalyteCodes: [],
      criteriaRows: [defaultCriteriaRow],
      normalRangeRows: [defaultNormalRangeRow],
      flagMessage: '',
    });
    setCutoffDialogSaveAttempted(false);
    setRatioSelectionProgressUnlocked(false);
  }, []);

  const handleCutoffDialogValueChange = useCallback((field, value) => {
    setCutoffDialogValues((prev) => {
      const nextValues = {
        ...prev,
      };
      let addedCriteriaRow = false;
      let addedNormalRangeRow = false;

      if (field === 'criteriaRows') {
        const incomingRows = Array.isArray(value) ? value : [defaultCriteriaRow];
        nextValues.criteriaRows = incomingRows.map((row) => ({ ...defaultCriteriaRow, ...row }));
        addedCriteriaRow = nextValues.criteriaRows.length > (prev.criteriaRows?.length || 0);
        if (nextValues.criteriaRows[0]?.criteria === 'all_patients') {
          nextValues.criteriaRows = [nextValues.criteriaRows[0]];
        }
      } else if (field === 'normalRangeRows') {
        const incomingRows = Array.isArray(value) ? value : [defaultNormalRangeRow];
        nextValues.normalRangeRows = incomingRows.slice(0, 1).map((row) => ({ ...defaultNormalRangeRow, ...row }));
      } else if (field === 'analyteCodes') {
        const incomingAnalyteCodes = Array.isArray(value) ? value : [];
        nextValues.analyteCodes = incomingAnalyteCodes;
      } else if (field === 'ratioNumeratorAnalyteCodes') {
        const incomingNumeratorCodes = Array.isArray(value) ? value : [];
        nextValues.ratioNumeratorAnalyteCodes = incomingNumeratorCodes;
      } else if (field === 'ratioDenominatorAnalyteCodes') {
        const incomingDenominatorCodes = Array.isArray(value) ? value : [];
        nextValues.ratioDenominatorAnalyteCodes = incomingDenominatorCodes;
      } else {
        nextValues[field] = value;
      }

      if (field === 'cutoffType') {
        setRatioSelectionProgressUnlocked(false);
        nextValues.analyteCode = '';
        nextValues.analyteCodes = [];
        nextValues.ratioNumeratorType = '';
        nextValues.ratioNumeratorAnalyteCode = '';
        nextValues.ratioNumeratorAnalyteCodes = [];
        nextValues.ratioDenominatorType = '';
        nextValues.ratioDenominatorAnalyteCode = '';
        nextValues.ratioDenominatorAnalyteCodes = [];
        nextValues.criteriaRows = [defaultCriteriaRow];
        nextValues.normalRangeRows = [defaultNormalRangeRow];
      }

      if (field === 'analyteCode' && !value) {
        nextValues.criteriaRows = [defaultCriteriaRow];
        nextValues.normalRangeRows = [defaultNormalRangeRow];
      }

      if (field === 'analyteCodes' && Array.isArray(value) && value.length < 2) {
        nextValues.criteriaRows = [defaultCriteriaRow];
        nextValues.normalRangeRows = [defaultNormalRangeRow];
      }

      if (field === 'ratioNumeratorType') {
        nextValues.ratioNumeratorAnalyteCode = '';
        nextValues.ratioNumeratorAnalyteCodes = [];
        if (!ratioSelectionProgressUnlocked) {
          nextValues.ratioDenominatorType = '';
          nextValues.ratioDenominatorAnalyteCode = '';
          nextValues.ratioDenominatorAnalyteCodes = [];
          nextValues.criteriaRows = [defaultCriteriaRow];
          nextValues.normalRangeRows = [defaultNormalRangeRow];
        }
      }

      if (field === 'ratioNumeratorAnalyteCode') {
        if (!ratioSelectionProgressUnlocked) {
          nextValues.ratioDenominatorType = '';
          nextValues.ratioDenominatorAnalyteCode = '';
          nextValues.ratioDenominatorAnalyteCodes = [];
          nextValues.criteriaRows = [defaultCriteriaRow];
          nextValues.normalRangeRows = [defaultNormalRangeRow];
        }
      }

      if (field === 'ratioNumeratorAnalyteCodes') {
        if (!ratioSelectionProgressUnlocked) {
          nextValues.ratioDenominatorType = '';
          nextValues.ratioDenominatorAnalyteCode = '';
          nextValues.ratioDenominatorAnalyteCodes = [];
          nextValues.criteriaRows = [defaultCriteriaRow];
          nextValues.normalRangeRows = [defaultNormalRangeRow];
        }
      }

      if (field === 'ratioDenominatorType') {
        nextValues.ratioDenominatorAnalyteCode = '';
        nextValues.ratioDenominatorAnalyteCodes = [];
        if (!ratioSelectionProgressUnlocked) {
          nextValues.criteriaRows = [defaultCriteriaRow];
          nextValues.normalRangeRows = [defaultNormalRangeRow];
        }
      }

      if (field === 'ratioDenominatorAnalyteCode' && !value) {
        nextValues.criteriaRows = [defaultCriteriaRow];
        nextValues.normalRangeRows = [defaultNormalRangeRow];
      }

      if (field === 'ratioDenominatorAnalyteCodes' && Array.isArray(value) && value.length < 2) {
        nextValues.criteriaRows = [defaultCriteriaRow];
        nextValues.normalRangeRows = [defaultNormalRangeRow];
      }

      if (!ratioSelectionProgressUnlocked && nextValues.cutoffType === 'ratio') {
        const numeratorReady = (
          (nextValues.ratioNumeratorType === 'single_analyte' && !!nextValues.ratioNumeratorAnalyteCode)
          || (nextValues.ratioNumeratorType === 'sum'
            && Array.isArray(nextValues.ratioNumeratorAnalyteCodes)
            && nextValues.ratioNumeratorAnalyteCodes.length >= 2)
        );
        const denominatorReady = (
          (nextValues.ratioDenominatorType === 'single_analyte' && !!nextValues.ratioDenominatorAnalyteCode)
          || (nextValues.ratioDenominatorType === 'sum'
            && Array.isArray(nextValues.ratioDenominatorAnalyteCodes)
            && nextValues.ratioDenominatorAnalyteCodes.length >= 2)
        );

        if (numeratorReady && denominatorReady) {
          setRatioSelectionProgressUnlocked(true);
        }
      }

      if (cutoffDialogSaveAttempted && !addedCriteriaRow && !addedNormalRangeRow) {
        const nextErrors = validateCutoffDialog(nextValues);
        setCutoffDialogErrors((prevErrors) => {
          const preservedKeys = Object.keys(prevErrors);
          const filteredErrors = {};

          preservedKeys.forEach((key) => {
            if (nextErrors[key]) {
              filteredErrors[key] = nextErrors[key];
            }
          });

          return filteredErrors;
        });
      }

      return nextValues;
    });
  }, [cutoffDialogSaveAttempted, ratioSelectionProgressUnlocked, validateCutoffDialog]);

  const handleSaveCutoffDialog = useCallback(() => {
    setCutoffDialogSaveAttempted(true);
    const validationErrors = validateCutoffDialog(cutoffDialogValues);
    if (Object.keys(validationErrors).length > 0) {
      setCutoffDialogErrors(validationErrors);
      return;
    }

    const mappedCutoffType = cutoffTypeUiToApiValue[cutoffDialogValues.cutoffType];
    const ratioNumeratorCodes = cutoffDialogValues.ratioNumeratorType === 'sum'
      ? (Array.isArray(cutoffDialogValues.ratioNumeratorAnalyteCodes) ? cutoffDialogValues.ratioNumeratorAnalyteCodes : [])
      : (cutoffDialogValues.ratioNumeratorType === 'single_analyte' && cutoffDialogValues.ratioNumeratorAnalyteCode
        ? [cutoffDialogValues.ratioNumeratorAnalyteCode]
        : []);
    const ratioDenominatorCodes = cutoffDialogValues.ratioDenominatorType === 'sum'
      ? (Array.isArray(cutoffDialogValues.ratioDenominatorAnalyteCodes) ? cutoffDialogValues.ratioDenominatorAnalyteCodes : [])
      : (cutoffDialogValues.ratioDenominatorType === 'single_analyte' && cutoffDialogValues.ratioDenominatorAnalyteCode
        ? [cutoffDialogValues.ratioDenominatorAnalyteCode]
        : []);
    const selectedAnalyteCodes = cutoffDialogValues.cutoffType === 'sum'
      ? (Array.isArray(cutoffDialogValues.analyteCodes) ? cutoffDialogValues.analyteCodes : [])
      : (cutoffDialogValues.cutoffType === 'single_analyte' && cutoffDialogValues.analyteCode
        ? [cutoffDialogValues.analyteCode]
        : [...new Set([...ratioNumeratorCodes, ...ratioDenominatorCodes])]);
    const criteriaRows = Array.isArray(cutoffDialogValues.criteriaRows) ? cutoffDialogValues.criteriaRows : [];
    const normalRangeRows = Array.isArray(cutoffDialogValues.normalRangeRows) && cutoffDialogValues.normalRangeRows.length
      ? cutoffDialogValues.normalRangeRows
      : [defaultNormalRangeRow];

    const criteriaKeys = serializeCriteriaToKeys(criteriaRows);
    const normalRangeString = serializeNormalRangeKey(normalRangeRows);
    const selectedConditionKey = getConditionKey(cutoffDialogCondition);

    // Treat as a temp condition when the dialog was opened from a temporary
    // (Add Condition) row, or when the condition is not yet present in the
    // working cutoff set. A stale empty-profile entry may already exist for the
    // same key (e.g. after deleting the last cutoff of a re-added condition), so
    // temp handling rebuilds the condition from the temp row's metadata and
    // drops any stale entry.
    const isTempCondition = !!cutoffDialogCondition?.isTempRow
      || !cutoffValues.some((c) => getConditionKey(c) === selectedConditionKey);

    let nextCutoffValues;
    if (isTempCondition) {
      // Create a new condition entry with the cutoff profile.
      // Strip out UI-only computed fields that must not be sent to the API.
      // eslint-disable-next-line no-unused-vars
      const { isTempRow: _isTempRow, conditionSortValue: _conditionSortValue, cutoffCount: _cutoffCount, ...conditionBase } = cutoffDialogCondition || {};
      const newCondition = { ...conditionBase, enabled: true };
      const conditionProfile = [];

      if (cutoffDialogValues.cutoffType === 'single_analyte') {
        const analyteCode1 = cutoffDialogValues.analyteCode || null;
        const primaryCodes = (newCondition.primaryAnalyte || newCondition.PrimaryAnalyte || '').split(',').map(s => s.trim()).filter(Boolean);
        const isPrimaryAnalyteMatch = analyteCode1 && primaryCodes.includes(analyteCode1);

        conditionProfile.push({
          priority: isPrimaryAnalyteMatch ? 1 : 2,
          cutoffType: 'value',
          flagMessage: cutoffDialogValues.flagMessage.trim(),
          analyteCode1,
          analyteCode2: null,
          birthWeight: criteriaKeys.birthWeight,
          collectionAge: criteriaKeys.collectionAge,
          gestationAge: criteriaKeys.gestationAge,
          normalRange: normalRangeString,
          enable: true,
          ed3nDefaultCutoff: false,
          isPendingSave: true,
        });
      } else {
        const numeratorCodeString = joinAnalyteCodes(ratioNumeratorCodes);
        const denominatorCodeString = joinAnalyteCodes(ratioDenominatorCodes);
        const sumCodeString = joinAnalyteCodes(selectedAnalyteCodes);
        const primaryAnalyteCodes = parseAnalyteCodeString(newCondition.primaryAnalyte || newCondition.PrimaryAnalyte || '');
        const ratioPriority = primaryAnalyteCodes.length > 0 && selectedAnalyteCodes.some((code) => primaryAnalyteCodes.includes(code)) ? 1 : 2;

        conditionProfile.push({
          priority: ratioPriority,
          cutoffType: mappedCutoffType,
          analyteCode1: cutoffDialogValues.cutoffType === 'ratio'
            ? numeratorCodeString
            : sumCodeString,
          analyteCode2: cutoffDialogValues.cutoffType === 'ratio'
            ? denominatorCodeString
            : null,
          analyteCodes: selectedAnalyteCodes,
          ratioNumeratorType: cutoffDialogValues.ratioNumeratorType || null,
          ratioNumeratorAnalyteCode: cutoffDialogValues.ratioNumeratorAnalyteCode || null,
          ratioNumeratorAnalyteCodes: ratioNumeratorCodes,
          ratioDenominatorType: cutoffDialogValues.ratioDenominatorType || null,
          ratioDenominatorAnalyteCode: cutoffDialogValues.ratioDenominatorAnalyteCode || null,
          ratioDenominatorAnalyteCodes: ratioDenominatorCodes,
          birthWeight: criteriaKeys.birthWeight,
          collectionAge: criteriaKeys.collectionAge,
          gestationAge: criteriaKeys.gestationAge,
          normalRange: normalRangeString,
          flagMessage: cutoffDialogValues.flagMessage.trim(),
          enable: true,
          ed3nDefaultCutoff: false,
          isPendingSave: true,
        });
      }

      newCondition.conditionProfile = conditionProfile;
      // Drop any stale entry for the same condition (e.g. an empty-profile row
      // left behind after deleting its last cutoff) so the temp row's metadata
      // (including its primary analyte) is the single source of truth.
      const withoutStaleEntry = cutoffValues.filter((c) => getConditionKey(c) !== selectedConditionKey);
      nextCutoffValues = [...withoutStaleEntry, newCondition];
    } else {
      nextCutoffValues = cutoffValues.map((condition) => {
        if (getConditionKey(condition) !== selectedConditionKey) {
          return condition;
        }

        const conditionProfile = Array.isArray(condition.conditionProfile || condition.ConditionProfile) ? [...(condition.conditionProfile || condition.ConditionProfile)] : [];
        let updatedProfile;

        if (cutoffDialogValues.cutoffType === 'single_analyte') {
          const analyteCode1 = cutoffDialogValues.analyteCode || null;
          const primaryCodes = (condition.primaryAnalyte || condition.PrimaryAnalyte || '').split(',').map(s => s.trim()).filter(Boolean);
          const isPrimaryAnalyteMatch = analyteCode1 && primaryCodes.includes(analyteCode1);

          updatedProfile = {
            priority: isPrimaryAnalyteMatch ? 1 : 2,
            cutoffType: 'value',
            flagMessage: cutoffDialogValues.flagMessage.trim(),
            analyteCode1,
            analyteCode2: null,
            birthWeight: criteriaKeys.birthWeight,
            collectionAge: criteriaKeys.collectionAge,
            gestationAge: criteriaKeys.gestationAge,
            normalRange: normalRangeString,
          };
        } else {
          const numeratorCodeString = joinAnalyteCodes(ratioNumeratorCodes);
          const denominatorCodeString = joinAnalyteCodes(ratioDenominatorCodes);
          const sumCodeString = joinAnalyteCodes(selectedAnalyteCodes);
          const primaryAnalyteCodes = parseAnalyteCodeString(condition.primaryAnalyte || condition.PrimaryAnalyte || '');
          const ratioPriority = primaryAnalyteCodes.length > 0 && selectedAnalyteCodes.some((code) => primaryAnalyteCodes.includes(code)) ? 1 : 2;

          updatedProfile = {
            priority: ratioPriority,
            cutoffType: mappedCutoffType,
            analyteCode1: cutoffDialogValues.cutoffType === 'ratio'
              ? numeratorCodeString
              : sumCodeString,
            analyteCode2: cutoffDialogValues.cutoffType === 'ratio'
              ? denominatorCodeString
              : null,
            analyteCodes: selectedAnalyteCodes,
            ratioNumeratorType: cutoffDialogValues.ratioNumeratorType || null,
            ratioNumeratorAnalyteCode: cutoffDialogValues.ratioNumeratorAnalyteCode || null,
            ratioNumeratorAnalyteCodes: ratioNumeratorCodes,
            ratioDenominatorType: cutoffDialogValues.ratioDenominatorType || null,
            ratioDenominatorAnalyteCode: cutoffDialogValues.ratioDenominatorAnalyteCode || null,
            ratioDenominatorAnalyteCodes: ratioDenominatorCodes,
            birthWeight: criteriaKeys.birthWeight,
            collectionAge: criteriaKeys.collectionAge,
            gestationAge: criteriaKeys.gestationAge,
            normalRange: normalRangeString,
            flagMessage: cutoffDialogValues.flagMessage.trim(),
          };
        }

        if (
          cutoffDialogMode === CUTOFF_DIALOG_MODE_MODIFY
          && cutoffDialogSelectedProfileIndex !== null
          && conditionProfile[cutoffDialogSelectedProfileIndex]
        ) {
          const mergedProfile = {
            ...conditionProfile[cutoffDialogSelectedProfileIndex],
            ...updatedProfile,
            isPendingSave: true,
          };

          conditionProfile[cutoffDialogSelectedProfileIndex] = mergedProfile;
        } else if (cutoffDialogMode === CUTOFF_DIALOG_MODE_ADD) {
          conditionProfile.push({
            ...updatedProfile,
            enable: true,
            ed3nDefaultCutoff: false,
            isPendingSave: true,
          });
        }

        return {
          ...condition,
          conditionProfile,
        };
      });
    }

    // Stage the change into the working cutoff set only. Nothing is persisted
    // here — the user must click Apply, which is the single source of truth for
    // saving cutoffs. initialCutoffValues is intentionally left unchanged so the
    // biochemical tab becomes dirty and Apply is enabled. The new/modified
    // profiles are flagged isPendingSave so the table highlights them as unsaved.
    if (isTempCondition && tempConditionRows && setTempConditionRows) {
      setTempConditionRows((prev) => prev.filter((tc) => getConditionKey(tc) !== selectedConditionKey));
    }
    setCutoffValues(nextCutoffValues);
    setBiochemicalError('');
    closeCutoffDialog();
  }, [
    cutoffValues,
    closeCutoffDialog,
    cutoffDialogCondition,
    cutoffDialogMode,
    cutoffDialogSelectedProfileIndex,
    cutoffDialogValues,
    setCutoffValues,
    setBiochemicalError,
    validateCutoffDialog,
    tempConditionRows,
    setTempConditionRows,
  ]);

  return {
    CUTOFF_DIALOG_MODE_ADD,
    CUTOFF_DIALOG_MODE_MODIFY,
    cutoffDialogOpen,
    cutoffDialogValues,
    cutoffDialogErrors,
    cutoffDialogSaving,
    openCutoffDialog,
    closeCutoffDialog,
    handleCutoffDialogValueChange,
    handleSaveCutoffDialog,
    getCutoffDialogTitle,
  };
};

export default useCutoffEditor;
